import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChild,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild
} from '@angular/core';
import {Observable, of, Subscription} from 'rxjs';
import {MatTableDataSource} from '@angular/material/table';
import {MatSort} from '@angular/material/sort';
import {MatPaginator} from '@angular/material/paginator';
import {SelectionModel} from '@angular/cdk/collections';
import {TableColumn} from '../../../interfaces/table.interface';
import {CdkTable} from '@angular/cdk/table';
import {SettingsService} from '../../../../services/settings.service';
import {NotificationService} from '../../../../services/notification.service';
import * as XLSX from 'xlsx';
import {Clipboard} from '@angular/cdk/clipboard';

@Component({
    selector: 'app-data-table',
    templateUrl: './data-table.component.html',
    styleUrls: ['./data-table.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class DataTableComponent implements OnInit, AfterViewInit, OnDestroy {

  @Input() tableData: any;
  @Input() tableData$: Observable<any>;
  @Input() extraTableColumns: TableColumn[];
  @Input() columnHeader: object;
  @Input() hasActionsColumn = true;
  @Input() allowTableColumnFromCache = true;

  listData: MatTableDataSource<any>;
  searchKey: string;

  @ViewChild(MatSort, {static: true}) sort: MatSort;
  @ViewChild(MatPaginator, {static: true}) paginator: MatPaginator;
  @ContentChild(TemplateRef, {static: true}) actions: TemplateRef<any>;
  @ViewChild(CdkTable) cdkTable!: CdkTable<any>;

  subscriptions: Subscription = new Subscription();

  @Input() showDownloadButtons = true;
  @Input() reportTitle = 'report';
  @Input() showLoading = true;
  @Input() showFilter = true;
  @Input() showCheckbox = false;
  @Input() pageSizeOptions: number[] = [10, 50, 100, 250];
  @Input() pageSize = 10;
  @Input() pageLength: number =0
  @Input() pageIndex: number =0

  selection = new SelectionModel<any>(true, []);
  @Output() selectedRows: EventEmitter<any[]> = new EventEmitter();
  @Output() pageChanged = new EventEmitter<{ pageIndex: number, pageSize: number }>();

  tableColumns: TableColumn[];
  cacheTableColumnKeywords: string[];

  settingsService = inject(SettingsService);
  notificationService = inject(NotificationService);
  clipboard = inject(Clipboard);
  changeDetectorRefs = inject(ChangeDetectorRef);

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['pageLength']) {
      console.log('pageLength updated:', this.pageLength);
    }
    if (changes['pageIndex']) {
      console.log('pageIndex updated:', this.pageIndex);
    }
  }

  ngOnInit(){

    if (this.tableData){
      this.tableData$ = of(this.tableData);
    }
    this.tableColumns = [
      ...Object.keys(this.columnHeader)?.map( (keyword, id) => {
        return {
          id,
          keyword,
          title: this.columnHeader[keyword],
          isActionColumn: keyword?.toLowerCase() === 'action' || keyword?.toLowerCase() === 'actions'
        };
      })
    ];

    this.cacheTableColumnKeywords = this.settingsService.getTableColumnKeywords(this.tableColumns);

  }

  ngAfterViewInit() {
    this.subscriptions.add(
      this.tableData$.subscribe(data => {
        if (data){
          this.listData = new MatTableDataSource(data);
          this.listData.sort = this.sort;
          this.listData.paginator = this.paginator;

          this.changeDetectorRefs.detectChanges();
          this.cdkTable.renderRows();
        }

      })
    );

    this.listData.sort = this.sort;
    this.listData.paginator = this.paginator;

    this.subscriptions.add(
      this.selection.changed.subscribe( () => {
        this.selectedRows.emit(this.selection.selected);
      })
    );
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }

  filterTable() {
    this.listData.filter = this.searchKey.trim().toLowerCase();
  }

  onSearchClear() {
    this.searchKey = '';
    this.filterTable();
  }

  /** Whether the number of selected elements matches the total number of rows. */
  isAllSelected() {
    const numSelected = this.selection.selected.length;
    const numRows = this.listData.data.length;
    return numSelected === numRows;
  }

  /** Selects all rows if they are not all selected; otherwise clear selection. */
  masterToggle() {
    if (this.isAllSelected()) {
      this.selection.clear();
      return;
    }

    this.selection.select(...this.listData.data);
    this.selectedRows.emit(this.selection.selected);
  }

  /** The label for the checkbox on the passed row */
  checkboxLabel(row?: any[]): string {
    if (!row) {
      return `${this.isAllSelected() ? 'deselect' : 'select'} all`;
    }
    return `${this.selection.isSelected(row) ? 'deselect' : 'select'} row `;
  }

  copyTable() {
    if (this.clipboard.copy(document.getElementById('table').innerText)){
      this.notificationService.message('Table Content Copied!');
    }else {
      this.notificationService.errorMessage('Copy Failed!');
    }
  }

  downloadExcelFile() {
    const dataToBeExported = [];
    const keys: string[] = Object.keys(this.columnHeader);

    // if (this.cacheTableColumns?.length === 0){
    //   keys = this.tableColumns?.map(d => d?.keyword);
    // }else {
    //   keys = this.cacheTableColumns?.map(d => d?.keyword);
    // }

    this.subscriptions.add(
      this.tableData$.subscribe( (dataList) => {
        if (dataList?.length > 0){
          dataList?.forEach( dataObject => dataToBeExported.push(this.pickFromObject(dataObject, keys)));
        }
      })
    );

    if (dataToBeExported?.length > 0){

      const ws: XLSX.WorkSheet = XLSX.utils.json_to_sheet(dataToBeExported);
      /* generate workbook and add the worksheet */
      const wb: XLSX.WorkBook = XLSX.utils.book_new();

      XLSX.utils.book_append_sheet(wb, ws, 'amis-exported-data');

      /* save to file */
      XLSX.writeFile(wb, 'amis-data.xlsx');
    }
  }

  pickFromObject(object: object, keys: string[]): object{
    return keys.reduce((o, k) => {
      return Object.assign(o, { [k]: this.settingsService.getTableObjectValue(object, k) });
    }, {});
  }
onPageChange(event:any){
   this.pageChanged.emit({
    pageIndex: event.pageIndex,
    pageSize: event.pageSize
  });
  console.log(event)
}
}
